import {
  animate,
  animateChild,
  query,
  stagger,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Inject,
  INJECTOR,
  OnDestroy,
  OnInit,
  Optional,
  signal,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { menuBar, MenuBarButton, MenuBarDropdownContentDirective, MenuBarSplitButton, shellBottomContent, ShellStore } from '@softapps/core';
import { PageComponent, PageHeaderComponent, Panel } from '@softapps/core/pages';
import {
  ApplicationModule,
  ArchiveStore,
  BackNavigable,
  BackNavigationService,
  Command,
  CommandStore,
  handleRequestError,
  showRequestErrors,
  SOFTLINE_FEATURE_ARCHIVE,
  SOFTLINE_FEATURE_COMMANDS,
  SOFTLINE_FEATURE_MASTER_DATA,
  SOFTLINE_FEATURE_SHELL,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
  UploadFileCommand,
} from '@softline/application';
import {
  base64Encode,
  blobToDataUri,
  DownloadStore,
  ObjectStore,
  Patch,
  saveAs,
  SOFTLINE_FEATURE_DOWNLOAD,
  Store,
  Trace,
  ValidationMessage,
} from '@softline/core';
import {
  MessageBarStore,
  modalFn,
  ModalStore,
  PreviewService,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
  translator,
  Validators,
} from '@softline/ui-core';
import { DrawInputComponent, SignatureInputComponent } from '@softline/ui-core/draw';
import { SelectComponent, SelectOptionDirective } from '@softline/ui-core/combo-box';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  combineLatestWith,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { SOFTLINE_API_WORK_NOTES } from '../../arbeitsschein.api';
import {
  ArbeitsscheinConfig,
  SOFTLINE_CONFIG_ARBEITSSCHEIN,
} from '../../arbeitsschein.config';
import {
  SOFTLINE_FEATURE_WORK_NOTE_BEWEGUNGEN,
  SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
  SOFTLINE_FEATURE_WORK_NOTE_KZWETTER,
  WorkNoteBewegungenStore,
  WorkNoteCollectionStore,
  WorkNoteKzWetterStore,
} from '../../arbeitsschein.shared';
import { WorkNoteItemCardComponent } from '../../components/work-note-item-card/work-note-item-card.component';
import {
  APPOINTMENT_TYPES,
  AppointmentType,
  definitionForAppointmentType,
} from '../../data/appointment-type';
import { Bewegung } from '../../data/bewegung';
import { CreateWorkNote } from '../../data/create-work-note';
import { Employee } from '../../data/employee';
import { WorkNote } from '../../data/work-note';
import { CreateWorkNoteDialogComponent } from '../../dialogs/create-work-note-dialog/create-work-note-dialog.component';
import { NoteDialogComponent } from '../../dialogs/note-dialog/note-dialog.component';
import { SaveDialogComponent } from '../../dialogs/save-dialog/save-dialog.component';
import { SelectEmployeesModalComponent } from '../../dialogs/select-employees-modal/select-employees-modal.component';
import { SignatureDialogComponent } from '../../dialogs/signature-dialog/signature-dialog.component';
import { LoadSignaturePipe } from '../../pipes/load-signature.pipe';
import { StringToBlobPipe } from '../../pipes/string-to-blob.pipe';

type FormModel = {
  temperatur_min?: number;
  temperatur_max?: number;
  kzWetter?: string;
};

import { ChipComponent, ChipGroupComponent, FormatPipe, IconComponent, LoadingSpinnerComponent, MultilineInputComponent, NumberInputComponent, TranslatePipe, ValidationContainerComponent, ValidationMessagePipe } from '@softline/ui-core';
import { UploadFileDialogComponent } from '../../../../../../../framework/application/src/lib/archive/components/dialogs/upload-file-dialog/upload-file-dialog.component';
@Component({
  // tslint:disable-next-line:component-selector
  selector: 'lib-work-note-detail-page',
  templateUrl: './work-note-detail-page.component.html',
  styleUrls: ['./work-note-detail-page.component.css'],
  //changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LoadSignaturePipe,
    WorkNoteItemCardComponent,
    ApplicationModule,
    StringToBlobPipe,
    PageComponent,
    PageHeaderComponent,
    Panel,
    ChipComponent,
    ChipGroupComponent,
    DrawInputComponent,
    FormatPipe,
    IconComponent,
    LoadingSpinnerComponent,
    MultilineInputComponent,
    NumberInputComponent,
    SelectComponent,
    SelectOptionDirective,
    SignatureInputComponent,
    TranslatePipe,
    ValidationContainerComponent,
    ValidationMessagePipe,
    MenuBarButton,
    MenuBarSplitButton,
    MenuBarDropdownContentDirective,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('shrinkOrGrow', [
      transition(':enter', [
        style({ opacity: 0, height: 0 }),
        animate('190ms ease-out', style({ opacity: 1, height: '*' })),
      ]),
      transition(':leave', [animate('190ms ease-out', style({ opacity: 0, height: 0 }))]),
    ]),
    trigger('fade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('.44s ease'),
      ]),
    ]),
    trigger('stagger', [
      transition(':enter', [query(':enter', stagger('.1s', [animateChild()]))]),
    ]),
  ],
})
export class WorkNoteDetailPageComponent implements OnInit, OnDestroy, BackNavigable {
  public GOOGLE_LINK = 'https://www.google.com/maps/search/?api=1&query=';

  private routeSubscription?: Subscription;
  private workNoteSubscription?: Subscription;
  private formSubscription?: Subscription;

  readonly #modal = modalFn();
  readonly #t = translator();
  readonly #injector = inject(INJECTOR);

  readonly downloading = signal(false);

  private readonly masterData = this.store.signal(
    SOFTLINE_FEATURE_MASTER_DATA,
    ObjectStore.getters.data
  );

  readonly allEmployees = computed(() => {
    const masterData = this.masterData();

    if ('mitarbeiterInAbteilungModule' in masterData) {
      return masterData?.mitarbeiterInAbteilungModule as Employee[];
    }

    return [];
  });

  private formValidationMessages$ = new BehaviorSubject<{
    [field: string]: { isValid: boolean; messages?: ValidationMessage[] };
  }>({});

  readonly vermerkLocal = signal<string | null>(null);
  readonly vermerkInternLocal = signal<string | null>(null);
  readonly isSaving$ = new BehaviorSubject<boolean>(false);

  readonly isActionMenuOpen$ = this.store.observe(
    SOFTLINE_FEATURE_SHELL,
    ShellStore.getters.actionMenuOpen
  );

  readonly kzWetterOptions$ = this.store.observe(
    SOFTLINE_FEATURE_WORK_NOTE_KZWETTER,
    WorkNoteKzWetterStore.getters.all
  );

  readonly workNote$: Observable<WorkNote> = this.activatedRoute.paramMap.pipe(
    map((o) => o.get('id')),
    switchMap((id) =>
      this.store.observe(
        SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
        WorkNoteCollectionStore.getters.entity,
        +(id ?? -1)
      )
    )
  );

  readonly archiveKey$: Observable<any> = this.workNote$.pipe(
    map((o) => base64Encode(JSON.stringify(o?.archiveKey) + ''))
  );

  title$ = this.workNote$.pipe(map((o) => 'Arbeitsschein ' + o?.nummer));

  readonly bewegungen$: Observable<Bewegung[]> = this.store.observe(
    SOFTLINE_FEATURE_WORK_NOTE_BEWEGUNGEN,
    WorkNoteBewegungenStore.getters.all
  );

  readonly loading$ = this.store.observe(
    SOFTLINE_FEATURE_WORK_NOTE_BEWEGUNGEN,
    WorkNoteBewegungenStore.getters.loading
  );

  readonly readonly$ = this.workNote$.pipe(
    map((o) => (!!o?.status ? o?.status !== 'O' : false) || o?.kzmuster === 'J')
  );

  readonly hasImages$ = this.workNote$.pipe(
    switchMap((o) =>
      this.store.observe(
        SOFTLINE_FEATURE_ARCHIVE,
        ArchiveStore.getters.archive,
        this.convertArchiveKey(o.archiveKey)
      )
    ),
    map((o) => o?.length > 0)
  );

  isExpanded = false;

  readonly form = this.fb.group({
    temperatur_min: [undefined, [Validators.min(-50), Validators.max(50)]],
    temperatur_max: [undefined, [Validators.min(-50), Validators.max(50)]],
    kzWetter: [undefined],
  });

  readonly trackByFn = (index: number, note: WorkNote): string | number =>
    note?.id ?? index;

  readonly appointmentTypeKeys = APPOINTMENT_TYPES;

  readonly canSubmit = toSignal(
    this.workNote$.pipe(
      combineLatestWith(this.isSaving$),
      map(([o, isSaving]) => !!o?.signature && !isSaving)
    ),
    { initialValue: false }
  );

  readonly submitButtonTemplate = viewChild.required('submitButtonTemplate', {
    read: TemplateRef,
  });

  readonly isReadonly = toSignal(this.readonly$);
  readonly workNote = toSignal(this.workNote$, { initialValue: undefined });

  readonly menuBarLeadingTemplate = viewChild.required('menuBarLeadingTemplate', {
    read: TemplateRef,
  });

  readonly t = translator();

  readonly menuBarTrailingTemplate = viewChild.required('menuBarTrailingTemplate', {
    read: TemplateRef,
  });

  constructor(
    @Optional()
    @Inject(SOFTLINE_CONFIG_ARBEITSSCHEIN)
    readonly workNoteConfig: ArbeitsscheinConfig,
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    protected router: Router,
    private readonly previewService: PreviewService,
    private backNavigationService: BackNavigationService
  ) {
    /**
     *
     *   {
     *           name: '#WORK_NOTE.DETAIL.SAVE_BTN',
     *           class: 'menu bottom-menu accent',
     *           isVisible: this.workNote$.pipe(
     *             combineLatestWith(this.readonly$),
     *             map(([o, readonly]) => !isDefined(o?.signature) && !readonly)
     *           ),
     *           canExecute: this.isSaving$.pipe(map((saving) => !saving)),
     *           execute: async () => {
     *             const workNote = await firstValueFrom(this.workNote$);
     *             if (!workNote) return;
     *             await this.save(workNote);
     *           },
     *         },
     *         {
     *           name: '#WORK_NOTE.DETAIL.SAVE_AND_FINISH_BTN',
     *           class: 'menu bottom-menu accent',
     *           isVisible: this.workNote$.pipe(
     *             combineLatestWith(this.readonly$),
     *             map(([o, readonly]) => isDefined(o?.signature) && !readonly)
     *           ),
     *           canExecute: this.isSaving$.pipe(map((saving) => !saving)),
     *           execute: async () => {
     *             const workNote = await firstValueFrom(this.workNote$);
     *             if (!workNote) return;
     *             await this.markAsDone(workNote);
     *           },
     *         },
     *         {
     *           name: '#WORK_NOTE.DETAIL.CLOSE_BTN',
     *           class: 'menu bottom-menu accent',
     *           isVisible: this.readonly$,
     *           canExecute: this.isSaving$.pipe(map((saving) => !saving)),
     *           execute: async () => {
     *             await this.navigateBack();
     *           },
     *         },
     */

    menuBar({
      template: this.menuBarLeadingTemplate,
      title: this.t('Aktionen'),
      key: 'gewerbe/arbteitsschein/work-note-detail',
      align: 'leading',
      separated: true,
    });

    menuBar({
      template: this.menuBarTrailingTemplate,
      title: this.t('Aktionen'),
      key: 'gewerbe/arbteitsschein/work-note-detail',
      align: 'trailing',
      separated: true,
    });

    shellBottomContent({
      type: 'template',
      template: this.submitButtonTemplate,
      containerStyle: 'grouped',
    });
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);

    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#WORK_NOTE.LIST.TITLE'
    );

    this.routeSubscription = this.activatedRoute.paramMap
      .pipe(map((params) => params.get('id')))
      .subscribe(async (id) => await this.load(id));

    this.workNoteSubscription = this.workNote$
      .pipe(filter((o) => !!o))
      .subscribe(async (workNote) => {
        await this.onWorknoteChanged(workNote);
      });

    this.formSubscription = this.form.valueChanges
      .pipe(withLatestFrom(this.workNote$))
      .subscribe(async ([formValue, entity]) => {
        if (!this.validateForm(formValue)) return;

        try {
          await this.syncFormValues(entity, formValue);
        } catch (e) {
          showRequestErrors(this.store, e);
        }
      });
  }

  @Trace({ callArgs: false })
  async onWorknoteChanged(workNote: WorkNote): Promise<void> {
    this.vermerkLocal.set(workNote?.vermerk ?? null);
    const { temperatur_min, temperatur_max, kzwetter, status, kzmuster } = workNote;

    this.form.patchValue(
      { temperatur_min, temperatur_max, kzWetter: kzwetter },
      { emitEvent: false }
    );

    if ((status && status !== 'O') || kzmuster === 'J') {
      this.form.disable();
    }

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: WorkNoteDetailPageComponent.name,
      commands: this.createCommands(workNote, status, kzmuster),
    });

    await this.store.dispatch(SOFTLINE_FEATURE_ARCHIVE, ArchiveStore.actions.read, {
      key: this.convertArchiveKey(workNote.archiveKey),
    });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription && !this.routeSubscription.closed)
      this.routeSubscription.unsubscribe();

    if (this.workNoteSubscription && !this.workNoteSubscription.closed)
      this.workNoteSubscription.unsubscribe();

    if (this.formSubscription && !this.formSubscription.closed)
      this.formSubscription.unsubscribe();

    this.routeSubscription = undefined;
    this.workNoteSubscription = undefined;
    this.formSubscription = undefined;

    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      WorkNoteDetailPageComponent.name
    );

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');

    this.backNavigationService.set(undefined);
  }

  async uploadFile(): Promise<void> {
    try {
      await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), {
        id: 'UPLOAD_FILE',
        component: UploadFileDialogComponent,
        data: {
          options: {
            sources: 'photo',
            selectionMode: 'multi',
            archiveKey: base64Encode(JSON.stringify(this.workNote()?.archiveKey)) ?? {
              path: '/v1/archive/upload',
            },
          },
        },
        dismiss: true,
      });
    } catch (e) {
      handleRequestError(e, this.#injector, { show: true });
    }
  }

  async downloadPDFPreview(): Promise<void> {
    const workNote = this.workNote();
    if (!workNote) return;

    try {
      this.downloading.set(true);

      const download = await this.store.dispatch(
        SOFTLINE_FEATURE_DOWNLOAD,
        DownloadStore.actions.download,
        {
          filename: `AS${workNote.nummer}.pdf`,
          location: {
            path: SOFTLINE_API_WORK_NOTES + `/${workNote.id}/print`,
          },
        }
      );

      this.downloading.set(false);
      saveAs(download.content, download.filename);
    } catch (e) {
      this.downloading.set(false);
      showRequestErrors(this.store, e);
    }
  }

  async copyWorkNote(): Promise<void> {
    const workNote = this.workNote();
    if (!workNote) return;

    await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<
        CreateWorkNote['belegdatum'] | null,
        {
          onSubmit: (
            params: CreateWorkNote['belegdatum']
          ) => Promise<{ workNote?: WorkNote }>;
        }
      >(),
      {
        component: CreateWorkNoteDialogComponent,
        id: 'CREATE_WORK_NOTE',
        data: {
          onSubmit: async (belegdatum: CreateWorkNote['belegdatum']) => {
            if (!belegdatum) return { workNote };

            try {
              const updatedWorkNote = await this.store.dispatch(
                SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
                WorkNoteCollectionStore.actions.createWorkNote,
                { belegdatum, arbeitsschein: workNote }
              );

              await this.router.navigate(['/arbeitsschein']);
              return { workNote: updatedWorkNote };
            } catch (e) {
              showRequestErrors(this.store, e);
              return { workNote };
            }
          },
        },
      }
    );
  }

  protected createCommands(
    workNote: WorkNote,
    status: WorkNote['status'],
    kzmuster: WorkNote['kzmuster']
  ): Command[] {
    if (!workNote || !workNote?.archiveKey) return [];

    const copyWorkNoteCommand: Command = {
      name: 'Kopieren',
      class: 'menu action-menu action-menu-top',
      icon: 'fa-regular fa-copy',
      isVisible: this.workNote$.pipe(map((o) => o?.kzmuster === 'J')),
      canExecute: this.workNote$.pipe(map((o) => o?.kzmuster === 'J')),
      execute: async () => {
        await this.store.dispatch(
          SOFTLINE_FEATURE_MODAL,
          ModalStore.actions.open<
            CreateWorkNote['belegdatum'] | null,
            {
              onSubmit: (
                params: CreateWorkNote['belegdatum']
              ) => Promise<{ workNote?: WorkNote }>;
            }
          >(),
          {
            component: CreateWorkNoteDialogComponent,
            id: 'CREATE_WORK_NOTE',
            data: {
              onSubmit: async (belegdatum: CreateWorkNote['belegdatum']) => {
                if (!belegdatum) return { workNote };

                try {
                  const updatedWorkNote = await this.store.dispatch(
                    SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
                    WorkNoteCollectionStore.actions.createWorkNote,
                    { belegdatum, arbeitsschein: workNote }
                  );

                  await this.router.navigate(['/arbeitsschein']);
                  return { workNote: updatedWorkNote };
                } catch (e) {
                  showRequestErrors(this.store, e);
                  return { workNote };
                }
              },
            },
          }
        );
      },
    };

    if (!status || status !== 'O' || kzmuster === 'J') {
      return this.workNoteConfig?.showCopyAction ? [copyWorkNoteCommand] : [];
    }

    const commands: Command[] = [
      new UploadFileCommand(this.store, {
        sources: 'photo',
        selectionMode: 'multi',
        archiveKey: base64Encode(JSON.stringify(workNote?.archiveKey)) ?? {
          path: '/v1/archive/upload',
        },
        class: 'menu action-menu action-menu-top',
        onFailure: (files) => {
          console.log('Upload Error');
        },
        onSuccess: async (files) => {
          await this.store.dispatch(
            SOFTLINE_FEATURE_ARCHIVE,
            ArchiveStore.actions.cancelAll
          );
          this.store.commit(
            SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
            WorkNoteCollectionStore.mutations.reloadArchiveKey,
            {
              id: workNote.id,
              archiveKey: workNote.archiveKey,
            }
          );
        },
      }),
    ];

    if (this.workNoteConfig?.showCopyAction && workNote?.kzmuster === 'J') {
      commands.push(copyWorkNoteCommand);
    }

    commands.push(
      ...[
        {
          name: 'Speichern',
          class: 'menu action-menu action-menu-top',
          icon: 'fa-regular fa-floppy-disk',
          canExecute: this.isSaving$.pipe(map((saving) => !saving)),
          execute: async () => {
            const entity = this.store.get(
              SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
              WorkNoteCollectionStore.getters.entity,
              +(this.activatedRoute.snapshot.paramMap.get('id') ?? -1)
            );

            if (!entity) return;

            await this.save(entity);
          },
        },
        {
          icon: 'fa-regular fa-eye',
          class: 'menu action-menu action-menu-top',
          name: 'Druckvorschau',
          execute: async () => {
            try {
              this.downloading.set(true);

              const download = await this.store.dispatch(
                SOFTLINE_FEATURE_DOWNLOAD,
                DownloadStore.actions.download,
                {
                  filename: `AS${workNote.nummer}.pdf`,
                  location: {
                    path: SOFTLINE_API_WORK_NOTES + `/${workNote.id}/print`,
                  },
                }
              );

              this.downloading.set(false);
              saveAs(download.content, download.filename);
            } catch (e) {
              this.downloading.set(false);
              showRequestErrors(this.store, e);
            }
          },
        },
        {
          name: 'Abschließen',
          icon: 'fa-regular fa-check',
          class: 'menu action-menu action-menu-top',
          canExecute: this.workNote$.pipe(
            combineLatestWith(this.isSaving$),
            map(([o, isSaving]) => !!o?.signature || !isSaving)
          ),
          execute: async () => {
            const entity = this.store.get(
              SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
              WorkNoteCollectionStore.getters.entity,
              +(this.activatedRoute.snapshot.paramMap.get('id') ?? -1)
            );

            if (!entity) return;

            await this.markAsDone(entity);
          },
        },
      ]
    );

    return commands;
  }

  async navigateBack() {
    await this.router.navigate(['/arbeitsschein'], { queryParamsHandling: 'preserve' });
  }

  getDefinitionForAppointmentType(type: AppointmentType) {
    return definitionForAppointmentType(type);
  }

  async updateAppointmentType(
    workNote: WorkNote,
    type: AppointmentType | undefined
  ): Promise<void> {
    if (!type) return;

    const patch: Patch<WorkNote> = {
      id: workNote.id,
      changes: {
        ...workNote,
        kzterminart: type,
      },
    };

    try {
      this.isSaving$.next(true);

      await this.store.dispatch(
        SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
        WorkNoteCollectionStore.actions.patch,
        { patch, pathParams: { id: workNote.id } }
      );
    } catch (e) {
      this.isSaving$.next(false);
      showRequestErrors(this.store, e);
    } finally {
      this.isSaving$.next(false);
    }
  }

  getGoogleLink(objekt: any): string | undefined {
    let link = this.GOOGLE_LINK;
    if (objekt.geodaten !== null) {
      link += objekt?.geodaten?.gpsbreite + ',' + objekt?.geodaten?.gpslaenge;
      return encodeURI(link);
    }
    return undefined;
  }

  async removeEmployee(workNote: WorkNote, employee: Employee): Promise<void> {
    await this.updateEmployees(
      workNote,
      workNote.mitarbeiter?.filter((o) => o.id !== employee.id) ?? []
    );
  }

  async addEmployeeModal(workNote: WorkNote): Promise<void> {
    const result = await this.#modal({
      title: 'Teilnehmer ändern',
      component: SelectEmployeesModalComponent,
      data: {
        employees: workNote?.mitarbeiter ?? [],
        allEmployees: this.allEmployees,
      },
    });

    if (!result || result === 'DISMISSED') return;
    await this.updateEmployees(workNote, result);
  }

  private async updateEmployees(
    workNote: WorkNote,
    employees: Employee[]
  ): Promise<void> {
    const patch: Patch<WorkNote> = {
      id: workNote.id,
      changes: {
        ...workNote,
        mitarbeiter: employees,
      },
    };

    try {
      this.isSaving$.next(true);

      await this.store.dispatch(
        SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
        WorkNoteCollectionStore.actions.patch,
        { patch, pathParams: { id: workNote.id } }
      );
    } catch (e) {
      this.isSaving$.next(false);
      showRequestErrors(this.store, e);
    } finally {
      this.isSaving$.next(false);
    }
  }

  async openNoteDialog(bewegung: Bewegung, workNote: WorkNote): Promise<void> {
    await this.#modal({
      id: 'EDIT_NOTE_DIALOG',
      title: this.#t('#WORK_NOTE.DIALOGS.NOTES.TITLE'),
      component: NoteDialogComponent,
      data: { workNote, bewegung },
      dismiss: false,
    });
  }

  async copyItem(bewegung: Bewegung, workNote: WorkNote): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<
        CreateWorkNote['belegdatum'] | null,
        {
          onSubmit: (
            params: CreateWorkNote['belegdatum']
          ) => Promise<{ workNote?: WorkNote }>;
        }
      >(),
      {
        component: CreateWorkNoteDialogComponent,
        id: 'CREATE_WORK_NOTE',
        data: {
          onSubmit: async (belegdatum: CreateWorkNote['belegdatum']) => {
            if (!belegdatum) return { workNote };

            try {
              const updatedWorkNote = await this.store.dispatch(
                SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
                WorkNoteCollectionStore.actions.copyBewe,
                { id: workNote.id, belegdatum, arbeitsscheinBewe: bewegung }
              );

              await this.load(updatedWorkNote.id);

              await this.store.dispatch(
                SOFTLINE_FEATURE_MESSAGE_BAR,
                MessageBarStore.actions.success,
                'Die Bewegung wurde erfolgreich kopiert!'
              );

              return { workNote: updatedWorkNote };
            } catch (e) {
              showRequestErrors(this.store, e);
              return { workNote };
            }
          },
        },
      }
    );
  }

  async fillAllSuggestedValues(id: string, bewegungen: Bewegung[]): Promise<void> {
    for (const bewegung of bewegungen) {
      await this.setDoneAmount(id, bewegung, bewegung.menge);
    }
  }

  async setDoneAmount(id: string, bewegung: Bewegung, quantity: number): Promise<void> {
    const entity: Bewegung = { ...bewegung, mengeErl: quantity };
    await this.store.dispatch(
      SOFTLINE_FEATURE_WORK_NOTE_BEWEGUNGEN,
      WorkNoteBewegungenStore.actions.setDoneAmount,
      { entity, id, idbewe: bewegung.id }
    );
  }

  async sign(workNote: WorkNote): Promise<void> {
    await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), {
      id: 'WORK_NOTE_SIGNATURE_DIALOG',
      data: { workNote },
      component: SignatureDialogComponent,
    });
  }

  async markAsDone(entity: WorkNote): Promise<void> {
    const bewegungen = this.store.get(
      SOFTLINE_FEATURE_WORK_NOTE_BEWEGUNGEN,
      WorkNoteBewegungenStore.getters.all
    );

    const dialogResult = await this.#modal({
      id: 'WORK_NOTE_SAVE_DIALOG',
      title: this.#t('#WORK_NOTE.DIALOGS.FINISH.TITLE'),
      component: SaveDialogComponent,
      data: {
        workNote: entity,
        quantitiesDone: bewegungen.every((bewe) => bewe?.menge <= bewe?.mengeErl),
      },
      dismiss: true,
    });

    if (dialogResult === 'DISMISSED') return;

    if (!dialogResult) {
      await this.save(entity);
      return;
    }

    try {
      await this.save(entity, false);
    } catch (e) {
      console.log('saving error: ', e);
    }

    const updatedEntity = {
      ...entity,
      kopfau: { ...entity.kopfau, email: dialogResult?.email },
    };

    await this.syncFormValues(entity, this.form.value, false);

    try {
      this.isSaving$.next(true);

      const result = await this.store.dispatch(
        SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
        WorkNoteCollectionStore.actions.complete,
        { entity: updatedEntity }
      );

      if (!result) {
        await this.showCompleteError();
        return;
      }

      await this.showCompleteSuccess();
      await this.navigateBack();
      this.isSaving$.next(false);
    } catch (e) {
      showRequestErrors(this.store, e);
    } finally {
      this.isSaving$.next(false);
    }
  }

  async drawInputChanged(workNote: WorkNote, drawResult?: Blob | null): Promise<void> {
    await this.save({
      ...workNote,
      zeichnung: drawResult ? await blobToDataUri(drawResult) : undefined,
    });
  }

  async showCompleteError(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.error,
      {
        title: '#WORK_NOTE.MESSAGES.DONE_ERROR_SUBJECT',
        message: '#WORK_NOTE.MESSAGES.DONE_ERROR_MESSAGE',
      }
    );
  }

  async showCompleteSuccess(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.success,
      {
        title: '#WORK_NOTE.MESSAGES.DONE_SUCCESS_SUBJECT',
        message: '#WORK_NOTE.MESSAGES.DONE_SUCCESS_MESSAGE',
      }
    );
  }

  async save(entity: WorkNote, showSuccess = true): Promise<void> {
    if (this.validateForm(this.form.value)) {
      await this.syncFormValues(entity, this.form.value, showSuccess);
    } else {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        {
          title: '#WORK_NOTE.MESSAGES.SAVE_ERROR_TITLE',
          message: '#WORK_NOTE.MESSAGES.SAVE_ERROR_MESSAGE',
        }
      );
    }
  }

  convertArchiveKey(key: any): string {
    return base64Encode(JSON.stringify(key));
  }

  private async load(id: string | undefined | null): Promise<void> {
    if (!id) return;
    await Promise.all([
      this.store.dispatch(
        SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
        WorkNoteCollectionStore.actions.load,
        { id }
      ),
      this.store.dispatch(
        SOFTLINE_FEATURE_WORK_NOTE_BEWEGUNGEN,
        WorkNoteBewegungenStore.actions.loadMany,
        { pathParams: { id }, clear: true }
      ),
    ]);
  }

  async saveSignature(workNote: WorkNote, data: Blob | undefined | null): Promise<void> {
    try {
      const uploadResult = await this.store.dispatch(
        SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
        WorkNoteCollectionStore.actions.sign,
        { entity: workNote, signature: !data ? null : { files: [data] } }
      );

      if (!uploadResult) {
        await this.showUploadError();
        return;
      }

      await this.showUploadSuccess();
    } catch (e) {
      console.log(e);
      showRequestErrors(this.store, e);
    }
  }

  private async showUploadError(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.error,
      {
        title: 'Fehler',
        duration: 60000,
        message: 'Beim Speichern der Unterschrift ist ein Fehler aufgetreten!',
      }
    );
  }

  private async showUploadSuccess(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.success,
      {
        title: 'Unterschrift gespeichert',
        message: 'Die Unterschrift wurde erfolgreich gespeichert!',
      }
    );
  }

  async updateVermerk(vermerk: string | null, entity: WorkNote): Promise<void> {
    await this.syncFormValues(
      {
        ...entity,
        vermerk: vermerk ?? undefined,
      },
      this.form.value
    );
  }

  async updateVermerkIntern(
    vermerk_intern: string | null,
    entity: WorkNote
  ): Promise<void> {
    await this.syncFormValues(
      {
        ...entity,
        vermerk_intern: vermerk_intern ?? undefined,
      },
      this.form.value
    );
  }

  private async syncFormValues(
    entity: WorkNote,
    { temperatur_min, temperatur_max, kzWetter }: FormModel,
    showSuccessMessage = true
  ): Promise<void> {
    const patch: Patch<WorkNote> = {
      id: entity.id,
      changes: {
        ...entity,
        temperatur_min,
        temperatur_max,
        kzwetter: kzWetter,
      },
    };

    if (this.workNoteConfig?.showVermerkInput) {
      patch.changes['vermerk'] = this.vermerkLocal() ?? undefined;
    }

    if (this.workNoteConfig?.showVermerkInternInput) {
      patch.changes['vermerk_intern'] = this.vermerkInternLocal() ?? undefined;
    }

    try {
      this.isSaving$.next(true);
      await this.store.dispatch(
        SOFTLINE_FEATURE_WORK_NOTE_COLLECTION,
        WorkNoteCollectionStore.actions.patch,
        { patch, pathParams: { id: entity.id } }
      );

      if (showSuccessMessage) {
        await this.store.dispatch(
          SOFTLINE_FEATURE_MESSAGE_BAR,
          MessageBarStore.actions.success,
          {
            title: '#WORK_NOTE.MESSAGES.SAVE_SUCCESS_TITLE',
            duration: 4000,
          }
        );
      }
    } catch (e) {
      this.isSaving$.next(false);
      showRequestErrors(this.store, e);
    } finally {
      this.isSaving$.next(false);
    }
  }

  private validateForm({ temperatur_min, temperatur_max }: FormModel): boolean {
    if (!!temperatur_min && isNaN(+temperatur_min)) {
      this.formValidationMessages$.next({
        ...this.formValidationMessages$.value,
        ['temperatur_min']: {
          isValid: false,
          messages: [
            {
              message: 'Es darf nur ein numerischer Wert eingegeben werden.',
              type: 'error',
              subject: 'Kein numerischer Wert',
            },
          ],
        },
      });
      return false;
    }

    if (temperatur_max && isNaN(+temperatur_max)) {
      this.formValidationMessages$.next({
        ...this.formValidationMessages$.value,
        ['temperatur_max']: {
          isValid: false,
          messages: [
            {
              message: 'Es darf nur ein numerischer Wert eingegeben werden.',
              type: 'error',
              subject: 'Kein numerischer Wert',
            },
          ],
        },
      });
      return false;
    }

    if (
      temperatur_min &&
      temperatur_max &&
      !isNaN(+temperatur_min) &&
      !isNaN(+temperatur_max) &&
      +temperatur_min > +temperatur_max &&
      this.form.valid
    ) {
      this.formValidationMessages$.next({
        ...this.formValidationMessages$.value,
        ['temperatur_min']: {
          isValid: false,
          messages: [
            {
              message: '',
              type: 'error',
              subject: 'Mindestwert darf nicht größer sein als Maximalwert',
            },
          ],
        },
      });
      return false;
    }

    if (
      this.form.valid &&
      !isNaN(+(temperatur_min ?? '')) &&
      !isNaN(+(temperatur_max ?? ''))
    ) {
      this.formValidationMessages$.next({
        ['temperatur_min']: {
          isValid: true,
          messages: [],
        },
        ['temperatur_max']: {
          isValid: true,
          messages: [],
        },
      });
      return true;
    } else if (!this.form.controls['temperatur_min'].valid) {
      this.formValidationMessages$.next({
        ...this.formValidationMessages$.value,
        ['temperatur_min']: {
          isValid: false,
          messages: [
            {
              message: '',
              type: 'error',
              subject: 'Nur Werte zwischen -50 und 50 sind zulässig',
            },
          ],
        },
      });
      return false;
    } else {
      this.formValidationMessages$.next({
        ...this.formValidationMessages$.value,
        ['temperatur_max']: {
          isValid: false,
          messages: [
            {
              message: 'Nur Werte zwischen -50 und 50 sind zulässig!',
              type: 'error',
              subject: 'Eingabe überprüfen',
            },
          ],
        },
      });
      return false;
    }
  }
}
